import { Badge, Col, Row } from 'reactstrap';
import moment from 'moment';
import { isEmpty } from 'lodash-es';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { Link } from '@s20/components/common/Link';
import { getMapUrl } from '@s20/common/util/dom';
import { AppNavPaths } from '../../../Common';
import { Skeleton } from '../../../common/Skeleton';
import { getEmployeeAddress, getEmployeeId, getEmployeeName } from './common';

export const Employee = ({
    selected, routeData, routeDataLoading = false, mapData: empMapData, ...emp
}) => {
    const address = getEmployeeAddress(emp);

    return (
        <>
            <div className="p-2">
                <Row className="mb-2">
                    <Col xs="12">
                        <div className="text-muted text-uppercase small">
                            Employee
                        </div>

                        <div className="font-weight-semibold">
                            {getEmployeeName(emp)}

                            <Link
                                to={`${AppNavPaths.Employee}/${getEmployeeId(emp)}`}
                                target="_blank"
                                className="ml-1"
                            >
                                <FontAwesomeIcon
                                    icon={faArrowUpRightFromSquare}
                                    size="sm"
                                />
                            </Link>
                        </div>
                    </Col>
                </Row>

                {Boolean(address) && (
                    <Row className="mb-2">
                        <Col xs="12">
                            <div className="text-muted text-uppercase small">
                                Address
                            </div>
                            <div className="font-weight-semibold text-break">
                                <a
                                    href={getMapUrl(address)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="site-link"
                                >
                                    {address}
                                </a>
                            </div>
                        </Col>
                    </Row>
                )}

                {(emp.dispatchLocation || emp.dispatchCompany) && (
                    <Row className="mb-2">
                        <Col xs="12">
                            <div className="text-muted text-uppercase small">
                                Dispatch Location
                            </div>
                            <div className="font-weight-semibold">
                                {emp.dispatchLocation || emp.dispatchCompany}
                            </div>
                        </Col>
                    </Row>
                )}


                {emp.assignmentDateTime && (
                    <Row className="mb-2">
                        <Col xs="12">
                            <div className="text-muted text-uppercase small">
                                Assignment Time
                            </div>
                            <div className="font-weight-semibold">
                                {moment(emp.assignmentDateTime).format('h:mm A')}
                            </div>
                        </Col>
                    </Row>
                )}

                {Boolean(empMapData) && (
                    <>
                        {empMapData?.certifications?.length > 0 && (
                            <Row className="mb-2">
                                <Col xs="12">
                                    <div className="text-muted text-uppercase small">Certifications</div>
                                    <div className="d-flex flex-wrap column-gap-2 row-gap-1 mt-1">
                                        {empMapData.certifications.map((cert) => (
                                            <Badge
                                                key={cert}
                                                className="mr-1"
                                                color="secondary"
                                            >{cert}</Badge>
                                        ))}
                                    </div>
                                </Col>
                            </Row>
                        )}

                        {empMapData?.userGroups?.length > 0 && (
                            <Row className="mb-2">
                                <Col xs="12">
                                    <div className="text-muted text-uppercase small">User Groups</div>
                                    <div className="d-flex flex-wrap column-gap-2 row-gap-1 mt-1 mr-1">
                                        {empMapData.userGroups.map((group) => (
                                            <Badge
                                                key={group}
                                                className="mr-1"
                                                color="secondary"
                                            >{group}</Badge>
                                        ))}
                                    </div>
                                </Col>
                            </Row>
                        )}


                        {(empMapData.scheduleStatus !== 'Unknown'
                            || (empMapData.jobNumbers.length > 0
                                && empMapData.confirmationStatus !== 'Unknown')) && (
                                <Row className="mb-2">
                                    {empMapData.scheduleStatus !== 'Unknown' && (
                                        <Col xs={empMapData.jobNumbers.length > 0 && empMapData.confirmationStatus !== 'Unknown' ? '6' : '12'}>
                                            <div className="text-muted text-uppercase small">
                                                Schedule
                                            </div>
                                            <div>
                                                <Badge color={empMapData.scheduleStatus === 'Scheduled' ? 'success' : 'secondary'}>
                                                    {empMapData.scheduleStatus}
                                                </Badge>
                                            </div>
                                        </Col>
                                    )}
                                    {empMapData.jobNumbers.length > 0
                                        && empMapData.confirmationStatus !== 'Unknown' && (
                                            <Col xs={empMapData.scheduleStatus !== 'Unknown' ? '6' : '12'}>
                                                <div className="text-muted text-uppercase small">
                                                    Confirmation
                                                </div>
                                                <div>
                                                    <Badge color={empMapData.confirmationStatus === 'Confirmed' ? 'success' : 'warning'}>
                                                        {empMapData.confirmationStatus}
                                                    </Badge>
                                                </div>
                                            </Col>
                                        )}
                                </Row>
                            )}

                        {empMapData.jobNumbers.length > 0 && (
                            <Row className="mb-2">
                                <Col xs="12">
                                    <div className="text-muted text-uppercase small">
                                        Jobs
                                    </div>
                                    <div className="font-weight-semibold">
                                        {empMapData.jobNumbers.join(', ')}
                                    </div>
                                </Col>
                            </Row>
                        )}

                        {empMapData.cancellations.length > 0 && (
                            <Row className="mb-2">
                                <Col xs="12">
                                    <div className="text-muted text-uppercase small">
                                        Cancellations
                                    </div>
                                    <div className="font-weight-semibold text-danger">
                                        {empMapData.cancellations.join(', ')}
                                    </div>
                                </Col>
                            </Row>
                        )}

                        {empMapData.attendance.length > 0 && (
                            <Row className="mb-2">
                                <Col xs="12">
                                    <div className="text-muted text-uppercase small">
                                        Daily Attendance
                                    </div>
                                    <div className="font-weight-semibold">
                                        {empMapData.attendance.join(', ')}
                                    </div>
                                </Col>
                            </Row>
                        )}

                        {empMapData.longTerm.length > 0 && (
                            <Row className="mb-2">
                                <Col xs="12">
                                    <div className="text-muted text-uppercase small">
                                        Long Term Status
                                    </div>
                                    <div className="font-weight-semibold text-warning">
                                        {empMapData.longTerm.join(', ')}
                                    </div>
                                </Col>
                            </Row>
                        )}
                    </>
                )}
            </div>

            {selected && routeDataLoading ? (
                <Row
                    tag="footer"
                    className="footer p-2"
                >
                    <Col
                        xs="6"
                        className="text-center"
                    >
                        <span className="d-flex align-items-center justify-content-center">
                            <Badge
                                color="secondary"
                                className="mr-1"
                            >
                                M
                            </Badge>
                            Distance
                        </span>

                        <div>
                            <Skeleton />
                        </div>
                    </Col>
                    <Col
                        xs="6"
                        className="text-center"
                    >
                        <span className="d-flex align-items-center justify-content-center">
                            <Badge
                                color="secondary"
                                className="mr-1"
                            >
                                M
                            </Badge>
                            Driving Time
                        </span>

                        <div>
                            <Skeleton />
                        </div>
                    </Col>
                </Row>
            ) : (
                <>
                    {selected && !isEmpty(routeData) && (
                        <Row
                            tag="footer"
                            className="footer p-2"
                        >
                            <Col
                                xs="6"
                                className="text-center"
                            >
                                <span className="d-flex align-items-center justify-content-center">
                                    <Badge
                                        color="secondary"
                                        className="mr-1"
                                    >
                                        M
                                    </Badge>
                                    Distance
                                </span>

                                <div>
                                    <Badge
                                        color="info"
                                        className="px-2"
                                    >
                                        {routeData.distanceInMiles}mi
                                    </Badge>
                                </div>
                            </Col>
                            <Col
                                xs="6"
                                className="text-center"
                            >
                                <span className="d-flex align-items-center justify-content-center">
                                    <Badge
                                        color="secondary"
                                        className="mr-1"
                                    >
                                        M
                                    </Badge>
                                    Driving Time
                                </span>

                                <div>
                                    <Badge
                                        color={routeData.duration < 1800 ? 'success' : 'warning'}
                                        className="px-2"
                                    >
                                        {routeData.humanReadableDuration}
                                    </Badge>
                                </div>
                            </Col>
                        </Row>
                    )}
                </>
            )}
        </>
    );
};